import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { Icon, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
const DEFAULT_TITLE = 'Add files here or click to upload';
const DEFAULT_ACCEPTED_TYPES = '.pdf,.xlsx,.docx,.eml,.msg';
export const FileDropZone = ({ onUpload, title = DEFAULT_TITLE, description, acceptedFileTypes = DEFAULT_ACCEPTED_TYPES, acceptedExtensions, maxFileSize = 25, maxFiles = 15, icon, disabled = false, }) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const extensionsDisplay = acceptedExtensions ?? acceptedFileTypes.split(',').map((ext) => ext.replace(/^\./, '').toUpperCase());
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isUploading && !disabled) {
            setIsDragging(true);
        }
    }, [isUploading, disabled]);
    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    }, []);
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (isUploading || disabled) {
            return;
        }
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length > 0) {
            handleFileUpload(files);
        }
    }, [isUploading, disabled]);
    const handleFileInputChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            handleFileUpload(files);
        }
    };
    const handleFileUpload = async (files) => {
        if (files.length > maxFiles) {
            addNotification({
                title: translate('Too many files'),
                subtitle: `${translate('Maximum')} ${maxFiles} ${translate('files allowed')}`,
                type: 'error',
            });
            return;
        }
        const maxFileSizeBytes = maxFileSize * 1024 * 1024;
        const oversizedFiles = files.filter((file) => file.size > maxFileSizeBytes);
        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map((f) => f.name).join(', ');
            addNotification({
                title: translate('File size exceeded'),
                subtitle: `${translate('The following files exceed the')} ${maxFileSize} MB ${translate('limit')}: ${fileNames}`,
                type: 'error',
            });
            return;
        }
        setIsUploading(true);
        try {
            await onUpload(files);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : translate('An error occurred while uploading files');
            addNotification({
                title: translate('Upload failed'),
                subtitle: message,
                type: 'error',
            });
        }
        finally {
            setIsUploading(false);
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }
    };
    const defaultIcon = (_jsx("span", { className: 'nucleus-text-primary-200', children: _jsx(Icon, { name: 'rectangleStack', color: 'primary', size: 60 }) }));
    const isInteractive = !isUploading && !disabled;
    return (_jsxs("div", { role: 'button', tabIndex: 0, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, onKeyDown: (e) => {
            if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                inputRef.current?.click();
            }
        }, className: `nucleus-relative nucleus-border-2 nucleus-border-dashed nucleus-rounded-lg nucleus-p-8 nucleus-transition-colors nucleus-flex nucleus-flex-col nucleus-items-center nucleus-justify-center nucleus-gap-6 nucleus-self-stretch ${isDragging
            ? 'nucleus-border-primary-600 nucleus-bg-primary-50'
            : 'nucleus-border-neutral-300 nucleus-bg-neutral-50'} ${!isInteractive ? 'nucleus-opacity-50 nucleus-cursor-not-allowed' : 'nucleus-cursor-pointer nucleus-hover:border-primary-600 nucleus-hover:bg-primary-50'}`, onClick: () => isInteractive && inputRef.current?.click(), children: [_jsx("input", { ref: inputRef, type: 'file', multiple: true, className: 'nucleus-sr-only', tabIndex: -1, "aria-hidden": true, onChange: handleFileInputChange, disabled: !isInteractive, accept: acceptedFileTypes }), _jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-items-center nucleus-justify-center nucleus-gap-6 nucleus-self-stretch', children: [icon ?? defaultIcon, _jsxs("div", { className: 'nucleus-text-center', children: [_jsx(Text, { as: 'p', className: 'nucleus-text-base nucleus-font-medium nucleus-text-primary-500', children: isUploading ? translate('Uploading...') : title }), _jsxs(Text, { as: 'p', size: 'sm', className: 'nucleus-mt-1 nucleus-text-neutral-500', children: [translate('Max'), " ", maxFiles, " ", translate('files, up to'), " ", maxFileSize, " MB ", translate('each')] }), _jsxs(Text, { as: 'p', size: 'xs', className: 'nucleus-mt-1 nucleus-text-neutral-400', children: [translate('Supported formats'), ": ", extensionsDisplay.join(', ')] }), description && (_jsx(Text, { as: 'p', size: 'sm', className: 'nucleus-mt-2 nucleus-text-neutral-500', children: description }))] })] })] }));
};
